'use client';

import { c as _c } from "react/compiler-runtime";
import { useContext, createContext } from 'react';
import { useI18n } from '~/features/i18n';
import { graphqlFieldErrors } from '~/utils/errors';
import type { NotificationDispatcher } from './NotificationDispatcher';
export const NotificationsContext = createContext<NotificationDispatcher>(null!);
export default function useNotifications() {
  const $ = _c(3);
  const notificationDispatcher = useContext(NotificationsContext);
  if (!notificationDispatcher) {
    throw new Error("This hook needs to be called inside a NotificationsContext provider");
  }
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n || $[1] !== notificationDispatcher) {
    t0 = {
      dispatchError(t1) {
        const data = t1 === undefined ? {} : t1;
        notificationDispatcher.dispatch({
          type: "ERROR",
          title: i18n._("Error"),
          message: i18n._("Oops, looks like something went wrong, please try again later."),
          ...data
        });
      },
      dispatchWarning(t2) {
        const data_0 = t2 === undefined ? {} : t2;
        notificationDispatcher.dispatch({
          type: "WARNING",
          title: i18n._("Warning"),
          message: i18n._("Careful, a warning was triggered."),
          ...data_0
        });
      },
      dispatchSuccess(t3) {
        const data_1 = t3 === undefined ? {} : t3;
        notificationDispatcher.dispatch({
          type: "SUCCESS",
          title: i18n._("Success"),
          message: i18n._("Your request was handled successfully."),
          ...data_1
        });
      },
      dispatchGraphqlError(t4, t5) {
        const error = t4 === undefined ? {} : t4;
        const data_2 = t5 === undefined ? {} : t5;
        let message = i18n._("Oops, looks like something went wrong, please try again later.");
        const [fieldError, ...otherFieldErrors] = graphqlFieldErrors(error);
        if (fieldError) {
          message = otherFieldErrors.length ? `${fieldError.message} (+${otherFieldErrors.length})` : fieldError.message;
        } else {
          if (error.response?.errors?.length > 0) {
            const firstError = error.response.errors[0];
            message = firstError.message;
          } else {
            if (error.message) {
              message = error.message;
            }
          }
        }
        return notificationDispatcher.dispatch({
          type: "ERROR",
          title: i18n._("Error"),
          message,
          ...data_2
        });
      },
      dispatch(data_3) {
        notificationDispatcher.dispatch(data_3);
      },
      removeNotification(notification) {
        notificationDispatcher.removeNotification(notification);
      }
    };
    $[0] = i18n;
    $[1] = notificationDispatcher;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0 as const;
}